import { useHandler } from "@redotech/react-util/hook";
import { SegmentNumberOperator } from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  RcoSegmentStep,
  rcoShipmentWeightSegmentSchema,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { WeightUnit } from "@redotech/redo-fulfillment-measurements-common/weight-conversion";
import { t, useTranslation } from "@redotech/redo-i18n";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

export const ShipmentWeightInspectorContent = memo(
  function ShipmentWeightInspectorContent({ step }: { step: RcoSegmentStep }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoShipmentWeightSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Condition")}
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          Shipment weight is {segmentNumberOperatorToText()[segment.operator]}{" "}
          {segment.weight} {segment.unit}
        </Text>
      </Flex>
    );
  },
);

export const EditableShipmentWeightInspectorContent = memo(
  function EditableShipmentWeightInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoShipmentWeightSegmentSchema.parse(step.segment),
      [step],
    );

    const handleOperatorChange = useHandler(
      (operator: SegmentNumberOperator) => {
        setStep({ ...step, segment: { ...segment, operator } });
      },
    );

    const handleUnitChange = useHandler((unit: WeightUnit) => {
      setStep({ ...step, segment: { ...segment, unit } });
    });

    const handleWeightChange = useHandler((value: string) => {
      const parsed = Number(value);
      const weight = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
      setStep({ ...step, segment: { ...segment, weight } });
    });

    const selectedOperator = useMemo(
      () => operatorOptions().find((o) => o.value === segment.operator),
      [segment.operator],
    );

    const selectedUnit = useMemo(
      () => unitOptions.find((o) => o.value === segment.unit),
      [segment.unit],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
        </Flex>
        <Flex dir="column" gap="md" w="full">
          <Flex align="center" dir="row" gap="md" wrap="wrap">
            <Text fontSize="xs" fontWeight="medium">
              {t("Shipment weight is")}
            </Text>
            <RedoSingleSelectDropdownInput<SegmentNumberOperator>
              fitToAnchor={false}
              flexGrow={false}
              options={operatorOptions()}
              optionSelected={({ item }) => handleOperatorChange(item.value)}
              placeholder={t("Select comparison")}
              selectedItem={selectedOperator}
              size="xs"
            />
          </Flex>
          <Flex align="center" dir="row" gap="sm">
            <RedoTextInput
              inputTypeHint="number"
              min={0}
              placeholder="0"
              setValue={handleWeightChange}
              size="sm"
              value={String(segment.weight)}
            />
            <RedoSingleSelectDropdownInput<WeightUnit>
              fitToAnchor={false}
              flexGrow={false}
              options={unitOptions}
              optionSelected={({ item }) => handleUnitChange(item.value)}
              placeholder={t("Unit")}
              selectedItem={selectedUnit}
              size="xs"
            />
          </Flex>
        </Flex>
      </>
    );
  },
);

function operatorOptions(): TextRedoListItem<SegmentNumberOperator>[] {
  return [
    {
      type: "text",
      id: SegmentNumberOperator.LESS_THAN,
      value: SegmentNumberOperator.LESS_THAN,
      text: t("less than"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
      value: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
      text: t("less than or equal to"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.EQUAL,
      value: SegmentNumberOperator.EQUAL,
      text: t("equal to"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.GREATER_THAN,
      value: SegmentNumberOperator.GREATER_THAN,
      text: t("greater than"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
      value: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
      text: t("greater than or equal to"),
    },
  ];
}

const unitOptions: TextRedoListItem<WeightUnit>[] = [
  { type: "text", id: WeightUnit.GRAMS, value: WeightUnit.GRAMS, text: "g" },
  {
    type: "text",
    id: WeightUnit.KILOGRAMS,
    value: WeightUnit.KILOGRAMS,
    text: "kg",
  },
  { type: "text", id: WeightUnit.OUNCES, value: WeightUnit.OUNCES, text: "oz" },
  { type: "text", id: WeightUnit.POUNDS, value: WeightUnit.POUNDS, text: "lb" },
  { type: "text", id: WeightUnit.TONNES, value: WeightUnit.TONNES, text: "t" },
];

function segmentNumberOperatorToText(): Record<SegmentNumberOperator, string> {
  return {
    [SegmentNumberOperator.LESS_THAN]: t("less than"),
    [SegmentNumberOperator.LESS_THAN_OR_EQUAL]: t("less than or equal to"),
    [SegmentNumberOperator.EQUAL]: t("equal to"),
    [SegmentNumberOperator.GREATER_THAN]: t("greater than"),
    [SegmentNumberOperator.GREATER_THAN_OR_EQUAL]: t(
      "greater than or equal to",
    ),
  };
}
